/**
 * AI CONTEXT
 * Purpose: student CRUD/search table.
 * Key deps: useStudentTableData, AddStudentFormModal, permission-based column filtering.
 * Referenced by: App route ROUTES.STUDENTS.
 * Importance: foundational student records used by most workflows.
 * Refactor: extract column-permission filter helper + standardize CRUD modal hook.
 */
import { useMemo, useState } from "react"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import {
  buildTableColumns,
  getColumnKey,
} from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import {
  STUDENT_SORTABLE_FIELDS,
  STUDENT_TABLE_COLUMNS,
} from "#constants/studentOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type StudentForTable } from "#customTypes/composedTypes/studentTypes"
import { SETTINGS_KEYS, Uuid } from "#customTypes/globalTypes"
import AddStudentFormModal from "#forms/AddStudent/AddStudentFormModal"
import useStudentTableData from "#hooks/dataHooks/useStudentTableData"
import useHasPermission from "#hooks/newHooks/useHasPermission"

const StudentsTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedStudentId, setSelectedStudentId] = useState<Uuid | undefined>()

  const {
    studentsForTable,
    isReady,
    errors,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  } = useStudentTableData()
  const canSeeStudents = useHasPermission(RESOURCES.STUDENT, USER_ACTIONS.READ)
  const canSeeContactInfo = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.VIEW_CONTACT_INFO
  )
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const columns = useMemo(() => {
    const keys = canSeeContactInfo
      ? STUDENT_TABLE_COLUMNS
      : STUDENT_TABLE_COLUMNS.filter(
          (col) =>
            getColumnKey(col) !== "guardian1Phone" &&
            getColumnKey(col) !== "guardian1Email"
        )

    return buildTableColumns<StudentForTable>(keys, {
      sortableFields: STUDENT_SORTABLE_FIELDS,
      showId: canViewId,
    })
  }, [canSeeContactInfo, canViewId])

  const handleOpen = () => {
    if (!canSeeStudents) {
      return
    }

    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedStudentId(undefined)
  }

  const handleRowClick = (selectedStudentId: string) => {
    setSelectedStudentId(selectedStudentId)
    handleOpen()
  }

  if (!canSeeStudents) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Students</h1>

        <TableFiltersRow
          onSearch={onSearch}
          onAdd={handleOpen}
          searchPlaceholder={`Search by name, dob, skill level`}
          settingsKey={SETTINGS_KEYS.STUDENTS}
        />

        {isOpen && (
          <AddStudentFormModal
            onClose={handleClose}
            studentId={selectedStudentId}
          />
        )}

        <Table2<StudentForTable>
          data={studentsForTable}
          columns={columns}
          onRowClick={handleRowClick}
          isLoading={!isReady}
          errors={errors}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default StudentsTable
